<template>
    <div class="notif-center">
        <Button
            class="p-button-text p-button-rounded notif-bell"
            :aria-label="bellLabel"
            :aria-expanded="panelVisible.toString()"
            aria-haspopup="true"
            @click="toggle($event)"
        >
            <i class="pi pi-bell"></i>
        </Button>
        <!-- El globo va FUERA del Button: .p-button lleva overflow:hidden (por el ripple) y, al asomar
             el globo por el borde del círculo, lo recortaba. Aquí lo posiciona .notif-center. -->
        <Badge v-if="unreadCount > 0" :value="badgeValue" severity="danger" class="notif-badge" />

        <OverlayPanel
            ref="panel"
            :dismissable="true"
            appendTo="body"
            class="notif-panel"
            @show="panelVisible = true"
            @hide="panelVisible = false"
        >
            <div class="notif-head flex align-items-center justify-content-between">
                <span class="font-semibold text-900">Notificaciones</span>
                <button v-if="unreadCount > 0" class="notif-link" @click="markAll">Marcar todas como leídas</button>
            </div>

            <div v-if="notifications.length === 0" class="notif-empty">
                <i class="pi pi-inbox"></i>
                <span>No tienes notificaciones</span>
            </div>

            <div v-else class="notif-list">
                <button
                    v-for="n in notifications"
                    :key="n.id"
                    class="notif-item"
                    :class="{ 'notif-item--unread': isUnread(n) }"
                    @click="open(n)"
                >
                    <span class="notif-item__icon"><i :class="['pi', topicIcon(n.topic)]"></i></span>
                    <span class="notif-item__body">
                        <span class="notif-item__title">
                            <span v-if="isUnread(n)" class="notif-dot" aria-hidden="true"></span>{{ n.title }}
                        </span>
                        <span class="notif-item__preview">{{ preview(n.body) }}</span>
                        <span class="notif-item__date">{{ formatDate(n.createdAt) }}</span>
                    </span>
                </button>
            </div>
        </OverlayPanel>

        <!-- Detalle del mensaje -->
        <Dialog
            v-model:visible="detailVisible"
            :modal="true"
            :draggable="false"
            :header="selected ? selected.title : ''"
            class="notif-detail"
            :style="{ width: '34rem' }"
            :breakpoints="{ '575px': '92vw' }"
        >
            <p class="notif-detail__body">{{ selected ? selected.body : '' }}</p>
            <span class="notif-detail__date">{{ selected ? formatDate(selected.createdAt) : '' }}</span>
            <template #footer>
                <Button label="Cerrar" class="p-button-text" @click="detailVisible = false" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import Badge from 'primevue/badge';
import Dialog from 'primevue/dialog';

const TOPIC_ICON = {
    SUBSCRIPTION_DATE: 'pi-calendar',
    SUBSCRIPTION_EDOCS: 'pi-file',
    SYSTEM_UPDATE: 'pi-sync',
    GENERAL: 'pi-megaphone',
};

export default {
    name: 'NotificationCenter',
    components: { Button, OverlayPanel, Badge, Dialog },
    data() {
        return { panelVisible: false, detailVisible: false, selected: null };
    },
    computed: {
        notifications() { return this.$store.getters.notifications; },
        unreadCount() { return this.$store.getters.unreadCount; },
        unreadIds() { return new Set(this.$store.getters.unreadNotifications.map((m) => m.id)); },
        badgeValue() { return this.unreadCount > 9 ? '9+' : String(this.unreadCount); },
        // El globo ya no está dentro del botón, así que el lector de pantalla recibe el conteo por aquí.
        bellLabel() { return this.unreadCount > 0 ? `Notificaciones, ${this.unreadCount} sin leer` : 'Notificaciones'; },
    },
    methods: {
        toggle(event) { this.$refs.panel.toggle(event); },
        isUnread(n) { return this.unreadIds.has(n.id); },
        topicIcon(topic) { return TOPIC_ICON[topic] || 'pi-bell'; },
        preview(body) {
            const t = (body || '').replace(/\s+/g, ' ').trim();
            return t.length > 90 ? t.slice(0, 90) + '…' : t;
        },
        formatDate(s) {
            if (!s) return '';
            // Backend: "DD/MM/YYYY HH:MM:SS" -> "DD/MM/YYYY HH:MM"
            const parts = String(s).split(' ');
            const time = (parts[1] || '').slice(0, 5);
            return time ? `${parts[0]} ${time}` : parts[0];
        },
        open(n) {
            this.$store.dispatch('markNotificationRead', n.id);
            this.selected = n;
            this.detailVisible = true;
        },
        markAll() { this.$store.dispatch('markAllNotificationsRead'); },
    },
};
</script>

<style scoped lang="scss">
.notif-center { display: inline-flex; position: relative; }
.notif-bell {
    position: relative;
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;                 /* sin el padding por defecto del Button: la campana queda centrada en el círculo */
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--text-color);
}
.notif-bell .pi-bell { font-size: 1.3rem; line-height: 1; }
/* El aro de foco ya no se queda pegado tras cerrar el panel con el ratón; se conserva para teclado (a11y). */
.notif-bell:focus { box-shadow: none; outline: none; }
.notif-bell:focus-visible { box-shadow: 0 0 0 0.2rem #c7d6f0; outline: none; }
/* Misma esquina de antes, pero medida desde .notif-center (sin overflow) y sin robarle el clic al botón. */
.notif-badge { position: absolute; top: 4px; right: 4px; transform: translate(35%, -35%); pointer-events: none; }

.notif-panel { width: 22rem; max-width: 92vw; border-radius: 14px; padding: 0.5rem; }
.notif-head { padding: 0.25rem 0.5rem 0.5rem; }
.notif-link { background: none; border: none; cursor: pointer; color: #18407F; font-size: 0.8rem; }
.notif-link:hover { text-decoration: underline; }
.notif-empty { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; color: var(--text-color-secondary); padding: 1.75rem 0; }
.notif-empty .pi { font-size: 1.6rem; }

.notif-list { max-height: 22rem; overflow-y: auto; }
.notif-item {
    display: flex; gap: 0.6rem; width: 100%; text-align: left; cursor: pointer;
    padding: 0.6rem 0.5rem; border: none; background: transparent; border-radius: 8px;
    transition: background 0.15s ease;
}
.notif-item:hover { background: var(--surface-100); }
.notif-item--unread { background: #eef4ff; }
.notif-item--unread:hover { background: #e3edff; }
.notif-item__icon {
    flex-shrink: 0; width: 2rem; height: 2rem; border-radius: 50%;
    background: var(--surface-100); display: inline-flex; align-items: center; justify-content: center; color: #18407F;
}
.notif-item__body { display: flex; flex-direction: column; min-width: 0; }
.notif-item__title { display: inline-flex; align-items: center; gap: 0.4rem; font-weight: 600; color: var(--text-color); font-size: 0.9rem; }
.notif-dot { width: 7px; height: 7px; border-radius: 50%; background: #f18700; flex-shrink: 0; }
.notif-item__preview { color: var(--text-color-secondary); font-size: 0.8rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.notif-item__date { color: var(--text-color-secondary); font-size: 0.72rem; margin-top: 0.15rem; }

.notif-detail__body { white-space: pre-line; line-height: 1.55; margin: 0; }
.notif-detail__date { display: block; margin-top: 0.75rem; color: var(--text-color-secondary); font-size: 0.78rem; }
</style>
